/**
 * High-level key management functions.
 *
 * Private key is never stored in plaintext. It is:
 *   1. Encrypted by a PBKDF2 key derived from the messaging password → encPwd
 *   2. Encrypted by a PBKDF2 key derived from the recovery phrase   → encRec
 * Both encrypted copies live in IndexedDB. The server only sees the public key.
 */

import { deriveKey }              from './derive';
import { encryptBytes, decryptBytes } from './protect';
import { saveKeys, loadKeys }     from './keystore';
import { generateRecoveryPhrase } from './wordlist';

// ── Key generation ────────────────────────────────────────────────────────────

/**
 * Generate a new ECDH keypair, encrypt the private key two ways,
 * store in IndexedDB, and return the public key JWK + recovery phrase.
 *
 * @param {string} userId         — used as PBKDF2 salt
 * @param {string} msgPassword    — messaging password (may differ from login password)
 * @returns {Promise<{ publicJwk: object, recoveryPhrase: string[] }>}
 */
export async function generateAndStoreKeypair(userId, msgPassword) {
  // 1. Generate ECDH P-256 keypair
  const keypair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey'],
  );

  // 2. Export keys
  const publicJwk    = await crypto.subtle.exportKey('jwk', keypair.publicKey);
  const privateJwk   = await crypto.subtle.exportKey('jwk', keypair.privateKey);
  const privateBytes = new TextEncoder().encode(JSON.stringify(privateJwk));

  // 3. Generate recovery phrase
  const recoveryPhrase  = generateRecoveryPhrase(12);
  const recoveryString  = recoveryPhrase.join(' ');

  // 4. Derive two wrapping keys (password + recovery phrase)
  const [pwdKey, recKey] = await Promise.all([
    deriveKey(msgPassword,   userId),
    deriveKey(recoveryString, userId),
  ]);

  // 5. Encrypt private key with both wrapping keys
  const [encPwd, encRec] = await Promise.all([
    encryptBytes(privateBytes, pwdKey),
    encryptBytes(privateBytes, recKey),
  ]);

  // 6. Store in IndexedDB — private key never sent to server
  await saveKeys(userId, { encPwd, encRec, publicJwk });

  return { publicJwk, recoveryPhrase };
}

// ── Key unlocking ─────────────────────────────────────────────────────────────

/**
 * Unlock the private key using the messaging password.
 * Returns a CryptoKey on success, null on wrong password.
 *
 * @param {string} userId
 * @param {string} msgPassword
 * @returns {Promise<CryptoKey|null>}
 */
export async function unlockWithPassword(userId, msgPassword) {
  const stored = await loadKeys(userId);
  if (!stored?.encPwd) return null;

  try {
    const pwdKey       = await deriveKey(msgPassword, userId);
    const privateBytes = await decryptBytes(stored.encPwd, pwdKey);
    const privateJwk   = JSON.parse(new TextDecoder().decode(privateBytes));
    return crypto.subtle.importKey(
      'jwk', privateJwk,
      { name: 'ECDH', namedCurve: 'P-256' },
      true, ['deriveKey'],
    );
  } catch {
    return null; // Wrong password or corrupted data
  }
}

/**
 * Unlock the private key using the recovery phrase.
 * Returns a CryptoKey on success, null on wrong phrase.
 *
 * @param {string} userId
 * @param {string} recoveryPhrase — space-separated words
 * @returns {Promise<CryptoKey|null>}
 */
export async function unlockWithRecovery(userId, recoveryPhrase) {
  const stored = await loadKeys(userId);
  if (!stored?.encRec) return null;

  try {
    const recKey       = await deriveKey(recoveryPhrase.trim(), userId);
    const privateBytes = await decryptBytes(stored.encRec, recKey);
    const privateJwk   = JSON.parse(new TextDecoder().decode(privateBytes));
    return crypto.subtle.importKey(
      'jwk', privateJwk,
      { name: 'ECDH', namedCurve: 'P-256' },
      true, ['deriveKey'],
    );
  } catch {
    return null; // Wrong phrase or corrupted data
  }
}

/**
 * Re-wrap and store the private key after recovery.
 * Use this after a successful unlockWithRecovery to also set a new password wrap.
 *
 * @param {string} userId
 * @param {CryptoKey} privateKey  — decrypted CryptoKey
 * @param {string} newMsgPassword
 * @param {string} existingRecoveryPhrase — kept unchanged
 */
export async function rewrapAfterRecovery(userId, privateKey, newMsgPassword, existingRecoveryPhrase) {
  const privateJwk   = await crypto.subtle.exportKey('jwk', privateKey);
  const privateBytes = new TextEncoder().encode(JSON.stringify(privateJwk));
  const publicJwk    = (await loadKeys(userId))?.publicJwk || null;

  const [pwdKey, recKey] = await Promise.all([
    deriveKey(newMsgPassword,              userId),
    deriveKey(existingRecoveryPhrase.trim(), userId),
  ]);

  const [encPwd, encRec] = await Promise.all([
    encryptBytes(privateBytes, pwdKey),
    encryptBytes(privateBytes, recKey),
  ]);

  await saveKeys(userId, { encPwd, encRec, publicJwk });
}
